import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import {
  Activity,
  Mail,
  Lock,
  User,
  ArrowRight,
  DollarSign,
  Video,
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ROUTES } from "@/routes";

type SignInPath = "provider" | "member";
type AppRole = "provider" | "member";

export default function Auth() {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<AppRole>("member");
  const [signInPath, setSignInPath] = useState<SignInPath>("member");
  const [activeTab, setActiveTab] = useState("signin");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showEmailNotConfirmedModal, setShowEmailNotConfirmedModal] = useState(false);
  const [unconfirmedEmail, setUnconfirmedEmail] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const { signUp, signIn } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPasswordError(null);
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const confirmPassword = formData.get("confirmPassword") as string;
    const displayName = formData.get("displayName") as string;

    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match");
      setIsLoading(false);
      return;
    }

    const { error } = await signUp(email, password, displayName, selectedRole);

    if (error) {
      toast({
        title: "Sign up failed",
        description: error.message,
        variant: "destructive",
      });
    } else {
      setShowConfirmationModal(true);
    }
    setIsLoading(false);
  };

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    const { error } = await signIn(email, password);

    if (error) {
      if (error.message?.toLowerCase().includes("email not confirmed")) {
        setUnconfirmedEmail(email);
        setShowEmailNotConfirmedModal(true);
      } else {
        toast({
          title: "Sign in failed",
          description: error.message,
          variant: "destructive",
        });
      }
      setIsLoading(false);
    } else {
      navigate(ROUTES.ROOT);
    }
  };

  const handleResendVerification = async () => {
    setIsResending(true);
    const { error } = await supabase.auth.resend({ type: "signup", email: unconfirmedEmail });
    if (error) {
      if (error.message?.toLowerCase().includes("rate limit")) {
        toast({
          title: "Please wait",
          description: "Too many attempts. Please wait a few minutes before trying again.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Failed to resend", description: error.message, variant: "destructive" });
      }
    } else {
      toast({ title: "Email sent", description: "A new verification email has been sent. Please check your inbox." });
      setShowEmailNotConfirmedModal(false);
    }
    setIsResending(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
      <Dialog open={showConfirmationModal} onOpenChange={setShowConfirmationModal}>
        <DialogContent className="sm:max-w-md p-8">
          <div className="flex flex-col gap-8 py-6">
            <DialogHeader className="flex flex-row items-start gap-4 space-y-0 text-left">
              <CheckCircle className="h-8 w-8 text-green-500 shrink-0 mt-0.5" />
              <div className="flex flex-col gap-3">
                <DialogTitle className="text-2xl">Account Created</DialogTitle>
                <DialogDescription className="text-base leading-relaxed">
                  We've sent a confirmation link to your email address. Please check your inbox and confirm your email{" "}
                  <b>before you can sign in.</b>
                </DialogDescription>
              </div>
            </DialogHeader>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowConfirmationModal(false)} className="w-full py-6 text-base font-semibold">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEmailNotConfirmedModal} onOpenChange={setShowEmailNotConfirmedModal}>
        <DialogContent className="sm:max-w-md p-8">
          <div className="flex flex-col gap-8 py-6">
            <DialogHeader className="flex flex-row items-start gap-4 space-y-0 text-left">
              <AlertTriangle className="h-8 w-8 text-warning shrink-0 mt-0.5" />
              <div className="flex flex-col gap-3">
                <DialogTitle className="text-2xl">Email Not Verified</DialogTitle>
                <DialogDescription className="text-base leading-relaxed">
                  Your email address <b>{unconfirmedEmail}</b> has not been verified yet. Please check your inbox for
                  the verification link we sent when you signed up. If you can't find it, you can request a new one
                  below.
                </DialogDescription>
              </div>
            </DialogHeader>
          </div>
          <DialogFooter className="flex flex-col gap-2 sm:flex-col">
            <Button
              onClick={handleResendVerification}
              disabled={isResending}
              className="w-full py-6 text-base font-semibold"
            >
              <Mail className="mr-2 h-4 w-4" />
              {isResending ? "Sending..." : "Resend Verification Email"}
            </Button>
            <Button variant="outline" onClick={() => setShowEmailNotConfirmedModal(false)} className="w-full">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl gradient-primary shadow-lg">
            <Activity className="h-7 w-7 text-primary-foreground" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-foreground">Vivien Health</h1>
            <p className="text-sm text-muted-foreground">Patient Intake Portal</p>
          </div>
        </div>

        {showForgotPassword ? (
          <Card className="border-border/50 shadow-xl">
            <CardHeader className="text-center pb-4">
              <CardTitle className="font-display text-xl">Reset Password</CardTitle>
              <CardDescription>Enter your email and we'll send you a reset link.</CardDescription>
            </CardHeader>
            <CardContent>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  setIsLoading(true);
                  const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
                    redirectTo: `${window.location.origin}/reset-password`,
                  });
                  if (error) {
                    toast({ title: "Error", description: error.message, variant: "destructive" });
                  } else {
                    toast({ title: "Check your email", description: "A password reset link has been sent." });
                  }
                  setIsLoading(false);
                }}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="you@example.com"
                      className="pl-10"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full gradient-primary" disabled={isLoading}>
                  {isLoading ? "Sending..." : "Send Reset Link"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button type="button" variant="ghost" className="w-full" onClick={() => setShowForgotPassword(false)}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Sign In
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border/50 shadow-xl">
            <CardHeader className="text-center pb-4">
              <CardTitle className="font-display text-xl">
                {activeTab === "signin"
                  ? signInPath === "provider"
                    ? "Provider Sign In"
                    : "Member / Loved One Sign In"
                  : selectedRole === "provider"
                    ? "Provider Sign Up"
                    : "Member / Loved One Sign Up"}
              </CardTitle>
              <CardDescription>
                {activeTab === "signin"
                  ? signInPath === "provider"
                    ? "Sign in to access your telehealth dashboard"
                    : "Sign in to access your health portal"
                  : "Create your account to get started"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="signin">Sign In</TabsTrigger>
                  <TabsTrigger value="signup">Sign Up</TabsTrigger>
                </TabsList>

                <TabsContent value="signin">
                  <form onSubmit={handleSignIn} className="space-y-4">
                    {/* Sign-in Path Selection */}
                    <div className="space-y-2">
                      <Label>I am a...</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setSignInPath("provider")}
                          className={`p-3 rounded-lg border-2 transition-all text-left ${
                            signInPath === "provider"
                              ? "border-primary bg-primary/5"
                              : "border-border hover:border-primary/50"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Video className="h-4 w-4 text-primary" />
                            <span className="font-medium text-sm text-foreground">Healthcare Provider</span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">Virtual visits & telehealth</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setSignInPath("member")}
                          className={`p-3 rounded-lg border-2 transition-all text-left ${
                            signInPath === "member"
                              ? "border-primary bg-primary/5"
                              : "border-border hover:border-primary/50"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-primary" />
                            <span className="font-medium text-sm text-foreground">Member / Loved One</span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">Patient or family caregiver</div>
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signin-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signin-email"
                          name="email"
                          type="email"
                          placeholder="doctor@example.com"
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signin-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signin-password"
                          name="password"
                          type="password"
                          placeholder="••••••••"
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-xs text-primary hover:underline"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <Button type="submit" className="w-full gradient-primary" disabled={isLoading}>
                      {isLoading ? "Signing in..." : "Sign In"}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>

                    {/* Demo Account Quick Login */}
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">Or try a demo</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isLoading}
                        onClick={async () => {
                          setIsLoading(true);
                          const { error } = await signIn("demo-provider@vivienhealth.com", "demo1234");
                          if (error) {
                            toast({ title: "Demo login failed", description: error.message, variant: "destructive" });
                            setIsLoading(false);
                          } else {
                            navigate(ROUTES.ROOT);
                          }
                        }}
                      >
                        <Video className="mr-1.5 h-3.5 w-3.5" />
                        Demo Provider
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isLoading}
                        onClick={async () => {
                          setIsLoading(true);
                          const { error } = await signIn("demo-member@vivienhealth.com", "demo1234");
                          if (error) {
                            toast({ title: "Demo login failed", description: error.message, variant: "destructive" });
                            setIsLoading(false);
                          } else {
                            navigate(ROUTES.ROOT);
                          }
                        }}
                      >
                        <User className="mr-1.5 h-3.5 w-3.5" />
                        Demo Member
                      </Button>
                    </div>
                  </form>
                </TabsContent>

                <TabsContent value="signup">
                  <form onSubmit={handleSignUp} className="space-y-4">
                    {/* Role Selection */}
                    <div className="space-y-2">
                      <Label>I am a...</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setSelectedRole("provider")}
                          className={`p-4 rounded-lg border-2 transition-all text-left ${
                            selectedRole === "provider"
                              ? "border-primary bg-primary/5"
                              : "border-border hover:border-primary/50"
                          }`}
                        >
                          <div className="font-medium text-foreground">Healthcare Provider</div>
                          <div className="text-xs text-muted-foreground mt-1">Doctor, nurse, or clinic staff</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedRole("member")}
                          className={`p-4 rounded-lg border-2 transition-all text-left ${
                            selectedRole === "member"
                              ? "border-primary bg-primary/5"
                              : "border-border hover:border-primary/50"
                          }`}
                        >
                          <div className="font-medium text-foreground">Member / Loved One</div>
                          <div className="text-xs text-muted-foreground mt-1">Patient or family caregiver</div>
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-name">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-name"
                          name="displayName"
                          type="text"
                          placeholder="Dr. Jane Smith"
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-email"
                          name="email"
                          type="email"
                          placeholder="doctor@example.com"
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-password"
                          name="password"
                          type="password"
                          placeholder="••••••••"
                          className="pl-10"
                          minLength={6}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-confirm-password">Confirm Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-confirm-password"
                          name="confirmPassword"
                          type="password"
                          placeholder="••••••••"
                          className={`pl-10 ${passwordError ? "border-destructive" : ""}`}
                          minLength={6}
                          required
                        />
                      </div>
                      {passwordError && <p className="text-sm text-destructive">{passwordError}</p>}
                    </div>
                    <Button type="submit" className="w-full gradient-primary" disabled={isLoading}>
                      {isLoading ? "Creating account..." : "Create Account"}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}

        {/* Virtual Visit / Provider Sign In */}
        <div className="mt-6 p-4 border border-primary/30 rounded-lg bg-primary/5">
          <Link
            to={ROUTES.AUTH}
            onClick={(e) => {
              e.preventDefault();
              setSignInPath("provider");
              setSelectedRole("provider");
              setActiveTab("signin");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center justify-between group hover:bg-primary/10 -m-4 p-4 rounded-lg transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/15 flex items-center justify-center">
                <Video className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground">Virtual Visit / Provider</p>
                <p className="text-xs text-muted-foreground">Sign in or sign up as a healthcare provider</p>
              </div>
            </div>
            <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
          </Link>
        </div>

        {/* RCM Portal Link */}
        <div className="mt-3 p-4 border border-border rounded-lg bg-card/50">
          <Link
            to={ROUTES.BILLING_AUTH}
            className="flex items-center justify-between group hover:bg-muted/50 -m-4 p-4 rounded-lg transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-success/15 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="font-medium text-foreground">Revenue Cycle Management</p>
                <p className="text-xs text-muted-foreground">Billing staff portal</p>
              </div>
            </div>
            <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
          </Link>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
}
